// src/lib/shopifyAppBridge.ts
import createApp, { type ClientApplication } from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge-utils';

const HOST_STORAGE_KEY = 'shopify_app_host';
let appBridgeInstance: ClientApplication | null = null;

export function resolveHost(): string | null {
  if (typeof window === 'undefined') return null;

  // 1. Session storage
  const storedHost = sessionStorage.getItem(HOST_STORAGE_KEY);
  if (storedHost) return storedHost;

  // 2. URL params
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host');
  if (host) return host;

  return null;
}
export function setStoredHost(host: string | null | undefined) {
  if (typeof window === 'undefined') {
    return;
  }
  if (host && host.trim() !== '') {
    sessionStorage.setItem(HOST_STORAGE_KEY, host);
  } else {
    // Clear stored host if empty
    sessionStorage.removeItem(HOST_STORAGE_KEY);
  }
}
// export function initAppBridge(): ClientApplication | null {
//   if (typeof window === 'undefined') return null;

//   const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY || '';
//   const host = resolveHost();

//   console.log('🟢 App Bridge init check');
//   console.log('Host from resolveHost():', host);
//   console.log('apiKey:', apiKey);

//   if (!apiKey || !host) {
//     console.warn('🚨 Missing apiKey or host. App Bridge not initialized.');
//     return null;
//   }

//   if (appBridgeInstance) return appBridgeInstance;

//   try {
//     appBridgeInstance = createApp({
//       apiKey,
//       host,
//       forceRedirect: false, // dev safe: avoid crash on INVALID_ORIGIN
//     });

//     console.log('✅ App Bridge instance created:', appBridgeInstance);
//     return appBridgeInstance;
//   } catch (err) {
//     console.error('🚨 App Bridge init failed:', err);
//     return null;
//   }
// }
// let appBridgeInstance: ClientApplication | null = null;
let initializing = false;

export function initAppBridge(): ClientApplication | null {
  if (typeof window === 'undefined') return null;

  // 🚫 Already created
  if (appBridgeInstance) {
    return appBridgeInstance;
  }

  // 🚫 Prevent parallel init
  if (initializing) {
    return null;
  }

  const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY || '';
  const host = resolveHost();

  if (!apiKey || !host) {
    // console.warn('🚨 Missing apiKey or host');
    return null;
  }

  // 🚫 Only inside Shopify iframe
  try {
    if (window.top === window.self) {
      // console.warn('⚠️ Not inside Shopify iframe, skipping App Bridge');
      return null;
    }
  } catch {
    // iframe cross-origin = OK
  }

  initializing = true;

  try {
    appBridgeInstance = createApp({
      apiKey,
      host,
      forceRedirect: false, // dev
    });

    console.log('✅ App Bridge created ONCE');
    return appBridgeInstance;
  } finally {
    initializing = false;
  }
}

export async function fetchSessionToken(): Promise<string | null> {
  const app = initAppBridge();
  if (!app) return null;

  try {
    return await getSessionToken(app);
  } catch (err) {
    console.error('🚨 Failed to fetch session token:', err);
    return null;
  }
}
export function decodeShopFromHost(host: string | null): string | null {
  if (!host) {
    return null;
  }

  try {
    // Decode the base64 host parameter
    const decoded = atob(host);
    console.log('🔍 Host decode debug:', { host, decoded });

    // Extract shop name from path (format: /store/shopname)
    const match = decoded.match(/\/store\/([a-z0-9][a-z0-9\-]*)/i);
    if (match && match[1]) {
      // Return full shop domain
      const shopDomain = `${match[1]}.myshopify.com`;
      console.log('✅ Decoded shop domain:', shopDomain);
      return shopDomain;
    } else {
      console.warn('❌ Could not extract shop name from decoded host:', decoded);
    }
  } catch (error) {
    console.warn('❌ Failed to decode shop from host parameter:', error);
    console.warn('Host parameter:', host);
  }

  return null;
}

/**
 * Get shop domain from multiple sources: URL params, decoded host, or current URL
 * This ensures shop domain is always available for API calls
 */
export function getShopFromSources(shopParam?: string | null, hostParam?: string | null): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // 1. Try explicit shop parameter first
  if (shopParam) {
    return shopParam;
  }

  // 2. Try to decode from host parameter
  const decodedShop = decodeShopFromHost(hostParam || resolveHost());
  if (decodedShop) {
    return decodedShop;
  }

  // 3. Try from current URL search params as fallback
  const params = new URLSearchParams(window.location.search);
  const shopFromUrl = params.get('shop');
  if (shopFromUrl) {
    return shopFromUrl;
  }

  return null;
}